import classNames from "@/utils/classNames";
import {
    forwardRef,
    TextareaHTMLAttributes,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

const DEFAULT_MAX_LENGTH = 255;
const DEFAULT_MAX_LENGTH_NOTICE_THRESHOLD = 80;

export default forwardRef(function TextAreaInput(
    {
        className = "",
        containerClassName = "",
        isFocused = false,
        maxLength = DEFAULT_MAX_LENGTH,
        minLength,
        characterCountColor = "text-brand-gray",
        height = 24,
        alwaysShowCharacterCount = false,
        ...props
    }: TextareaHTMLAttributes<HTMLTextAreaElement> & {
        containerClassName?: string;
        isFocused?: boolean;
        maxLength?: number;
        minLength?: number;
        height?: number | null;
        characterCountColor?: string;
        alwaysShowCharacterCount?: boolean;
    },
    ref,
) {
    const localRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => localRef.current?.focus(),
    }));

    const [uncontrolledText, setUncontrolledText] = useState(
        String(props.defaultValue ?? ""),
    );

    const text =
        typeof props.value === "string" ? props.value : uncontrolledText;
    const isBelowMinLength =
        minLength !== undefined &&
        !props.disabled &&
        text.trim().length < minLength;
    const isNearMaxLength =
        (text.length / maxLength) * 100 >= DEFAULT_MAX_LENGTH_NOTICE_THRESHOLD;
    const showCharacterCount =
        isBelowMinLength || isNearMaxLength || alwaysShowCharacterCount;

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        props.onChange?.(e);
        setUncontrolledText(e.target.value);
    };

    useEffect(() => {
        if (isFocused) {
            localRef.current?.focus();
        }
    }, []);

    return (
        <div className={classNames("flex flex-col", containerClassName)}>
            <textarea
                {...props}
                maxLength={maxLength}
                onChange={onChange}
                className={classNames(
                    "resize-none rounded-md outline-offset-1 border border-brand-border p-2 text-brand-gray shadow-xs focus:outline-brand-red focus:border-brand-red",
                    height !== null ? `!h-${height}` : "",
                    props.disabled ? "bg-gray-100" : "bg-white",
                    className,
                )}
                ref={localRef}
            />
            {showCharacterCount && (
                <p
                    className={classNames(
                        "self-end text-xs pt-1",
                        isBelowMinLength
                            ? "text-brand-red"
                            : characterCountColor,
                    )}
                >
                    {isBelowMinLength
                        ? `${text.trim().length}/${minLength}`
                        : `${text.length}/${maxLength}`}
                </p>
            )}
        </div>
    );
});
